/**
 * TemplatesPage — Radiologist Experience Framework
 *
 * Full-page template browser matching the reference design:
 * - Cards grouped by modality (MRI, CT, XR, US, …)
 * - Each card renders section content as a mini report preview
 * - My Templates / Public tabs
 * - Search bar
 * - List / Page view toggle
 * - + Create Template CTA (opens builder flow via RXF generate-preview endpoint)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import GridViewIcon from '@mui/icons-material/GridView';
import ViewListIcon from '@mui/icons-material/ViewList';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import PageHeaderNavigation from '../components/common/PageHeaderNavigation';
import templateService, { type ReportTemplate } from '../services/templateService';
import {
  generateTemplatePreview,
  saveRadiologistTemplate,
  type GenerateTemplateRequest,
} from '../services/rxfService';

// ── Sample content generator ──────────────────────────────────────────

const SAMPLE_DB: Record<string, Record<string, string[]>> = {
  EXAM: {
    default: ['{modality} {bodyRegion} — standard protocol'],
  },
  'CLINICAL INDICATION': {
    default: ['Evaluation for {bodyRegion} pain and screening for internal derangement.'],
    Head: ['Evaluation for persistent headache and intracranial screening.'],
    Chest: ['Evaluation for chest pain, dyspnoea, and screening for pulmonary pathology.'],
    Brain: ['Evaluation for persistent headache and intracranial screening.'],
    Knee: ['Evaluation for knee pain and screening for internal derangement.'],
    Shoulder: ['Evaluation for shoulder pain and screening for rotator cuff pathology.'],
    'Lumbar Spine': ['Evaluation for lower back pain and radiculopathy screening.'],
    Abdomen: ['Evaluation for acute abdomen, visceral pathology, and routine assessment.'],
  },
  'CLINICAL HISTORY': {
    default: ['Evaluation for {bodyRegion} pain and screening for internal derangement.'],
  },
  TECHNIQUE: {
    default: ['{modality} of the {bodyRegion} was performed using standard protocol.'],
    CT: ['Multiphase, multisequence computed tomography of the {bodyRegion}. Images were acquired in axial, sagittal, and coronal planes.'],
    MRI: ['Multiparametric, multisequence MRI of the {bodyRegion} was performed including T1W, T2W, PD FS, and STIR sequences.'],
    'X-ray': ['Plain radiograph of the {bodyRegion} — AP and lateral views obtained.'],
  },
  COMPARISON: {
    default: ['No prior imaging available for comparison.'],
  },
  FINDINGS: {
    default: ['No acute abnormality. Normal alignment and signal intensity.'],
    Brain: [
      'Grey–white matter differentiation is well-preserved. There is no evidence of intra- or extra-axial haemorrhage, mass effect, or midline shift.',
      'No acute infarct or abnormal enhancement is identified. The lateral, third, and fourth ventricles are normal in size, shape, and configuration.',
      'The basal cisterns and cortical sulci are patent. No skull base abnormality.',
    ],
    Knee: [
      'The ACL, PCL, MCL, and LCL are intact with normal fibre continuity and signal intensity.',
      'The medial and lateral menisci demonstrate normal morphology without tear. No meniscal extrusion or root tear.',
      'Articular cartilage is preserved without focal defect. No subchondral oedema or insufficiency fracture.',
      'Medial and lateral patellar retinacula are intact. The extensor mechanism is preserved.',
    ],
    Shoulder: [
      'The supraspinatus, infraspinatus, subscapularis, and teres minor tendons demonstrate normal morphology and signal intensity.',
      'No evidence of tendinosis, partial-thickness, or full-thickness tear.',
      'The long head of the biceps tendon is appropriately positioned within the bicipital groove.',
      'Labrum and rotator interval are intact. Glenohumeral cartilage is preserved.',
    ],
    'Lumbar Spine': [
      'Vertebral body heights and alignment are maintained. No acute compression fracture.',
      'Intervertebral disc T2 signal is preserved at all levels. No significant disc herniation or canal stenosis.',
      'The thecal sac and exiting nerve roots are normal in calibre and course.',
      'Posterior elements and paraspinal soft tissues are unremarkable.',
    ],
    Abdomen: [
      'The liver is normal in size and demonstrates smooth surface contours. The hepatic parenchyma shows uniform enhancement.',
      'No focal lesion or mass is identified. The intrahepatic biliary radicals and common bile duct are not dilated.',
      'The portal vein and hepatic veins are patent. The gallbladder is unremarkable.',
      'Pancreas, spleen, adrenal glands, and kidneys are normal in morphology and enhancement pattern.',
    ],
    Chest: [
      'The visualised portions of the lungs are clear. No focal consolidation, nodule, or pleural effusion.',
      'The mediastinum is central. Hilar and mediastinal lymph nodes are within normal limits.',
      'No acute or chronic parenchymal abnormality is identified in either lung.',
      'Normal HRCT of the thorax.',
    ],
  },
  IMPRESSION: {
    default: ['No acute abnormality identified.'],
    Brain: [
      'No acute intra- or extra-axial haemorrhage, mass effect, or midline shift.',
      'No evidence of infarction or abnormal enhancement.',
      'Normal HRCT of the brain.',
    ],
    Knee: [
      'No tear of the ACL, PCL, MCL, or LCL.',
      'No meniscal tear or chondral defect.',
      'Intact extensor mechanism and preserved articular cartilage.',
    ],
    Shoulder: [
      'Intact rotator cuff tendons — no tendinosis or tear.',
      'Normal biceps tendon position and glenohumeral cartilage.',
    ],
    'Lumbar Spine': [
      'No acute compression fracture or significant disc herniation.',
      'Normal spinal canal calibre and nerve root calibre.',
    ],
    Abdomen: [
      'Normal liver, gallbladder, pancreas, spleen, adrenal glands, and kidneys.',
      'No focal hepatic lesion or biliary dilatation.',
    ],
    Chest: [
      'No acute or chronic parenchymal abnormality in either lung.',
      'No evidence of mediastinal or hilar lymphadenopathy.',
    ],
  },
  ADVICE: {
    default: ['Clinical correlation is advised.'],
  },
};

function getSampleLines(section: string, modality: string, bodyRegion: string | null): string[] {
  const key = section.toUpperCase().trim();
  const db = SAMPLE_DB[key] || SAMPLE_DB[key.replace('CLINICAL ', '')] || {};
  // Try body-region specific first, then modality, then default
  const region = (bodyRegion || '').trim() || 'default';
  const lines = db[region] || db[modality] || db.default || ['—'];
  return lines.map((l) =>
    l
      .replace(/{modality}/g, modality)
      .replace(/{bodyRegion}/g, region === 'default' ? 'the region' : region),
  );
}

// ── Helpers ───────────────────────────────────────────────────────────

function parseSections(structureDef: string | null | undefined): string[] {
  if (!structureDef) return [];
  try {
    const parsed = JSON.parse(structureDef);
    if (Array.isArray(parsed?.sections)) return parsed.sections as string[];
  } catch { /* ignore */ }
  return [];
}

function modalityColor(modality: string | null | undefined): 'primary' | 'secondary' | 'success' | 'warning' | 'default' {
  switch ((modality || '').toUpperCase()) {
    case 'MRI': return 'primary';
    case 'CT': return 'secondary';
    case 'US': return 'success';
    case 'XR': case 'X-RAY': return 'warning';
    default: return 'default';
  }
}

const MODALITY_ORDER = ['MRI', 'CT', 'US', 'XR', 'NM', 'PET', 'Mammo', 'Fluoro'];

function groupByModality(templates: ReportTemplate[]): Map<string, ReportTemplate[]> {
  const map = new Map<string, ReportTemplate[]>();
  for (const t of templates) {
    const key = (t.modality || 'Other').toUpperCase();
    if (!map.has(key)) map.set(key, []);
    (map.get(key) ?? []).push(t);
  }
  // Sort modalities
  const sorted = new Map<string, ReportTemplate[]>();
  const keys = Array.from(map.keys()).sort((a, b) => {
    const ia = MODALITY_ORDER.indexOf(a);
    const ib = MODALITY_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
  for (const k of keys) sorted.set(k, map.get(k) ?? []);
  return sorted;
}

// ── Mini report preview card ──────────────────────────────────────────

const TemplateCard: React.FC<{ template: ReportTemplate }> = ({ template }) => {
  const [expanded, setExpanded] = useState(false);
  const sections = parseSections(template.structure_definition);
  const isAuto = template.is_custom_prompt === false;
  const color = modalityColor(template.modality);

  return (
    <Card
      sx={{
        width: 255,
        height: 360,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.15), 0 2px 4px rgba(0,0,0,0.12)' },
        overflow: 'hidden',
      }}
    >
      {/* Card header — report title */}
      <Box
        sx={{
          px: 2,
          pt: 1.5,
          pb: 0.75,
          borderBottom: '2px solid',
          borderColor: `${color}.main`,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '0.78rem',
              lineHeight: 1.2,
              color: 'text.primary',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {template.exam_name || template.template_name}
          </Typography>
          {template.cpt_code && (
            <Typography sx={{ fontSize: '0.68rem', color: 'text.disabled', mt: 0.25, display: 'block' }}>
              CPT {template.cpt_code}
            </Typography>
          )}
        </Box>
        {isAuto && (
          <Chip
            label="AUTO"
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: '0.62rem', px: 0.5, flexShrink: 0 }}
          />
        )}
      </Box>

      {/* Sections rendered as printed report */}
      <CardContent
        sx={{
          flex: 1,
          p: '12px 16px !important',
          overflow: expanded ? 'auto' : 'hidden',
        }}
      >
        {sections.length === 0 ? (
          <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic' }}>
            No section structure defined
          </Typography>
        ) : (
          <Box>
            {sections.map((section, i) => {
              const sampleLines = getSampleLines(
                section,
                template.modality || 'Radiology',
                template.body_region,
              );
              const showLines = expanded
                ? sampleLines
                : sampleLines.slice(
                    0,
                    section === 'FINDINGS' ? 3 : section === 'IMPRESSION' ? 2 : 1,
                  );
              return (
                <Box key={i} sx={{ mb: i === sections.length - 1 ? 0 : 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: `${color}.dark`,
                      display: 'block',
                      lineHeight: 1.3,
                      mb: 0.25,
                    }}
                  >
                    {section}
                  </Typography>
                  <Box>
                    {showLines.map((line, j) => (
                      <Typography
                        key={j}
                        sx={{
                          fontSize: '0.75rem',
                          lineHeight: 1.45,
                          color: 'text.primary',
                          display: 'block',
                        }}
                      >
                        {line}
                      </Typography>
                    ))}
                    {!expanded && sampleLines.length > showLines.length && (
                      <Typography
                        sx={{
                          fontSize: '0.65rem',
                          color: 'text.disabled',
                          fontStyle: 'italic',
                          display: 'block',
                          mt: 0.25,
                        }}
                      >
                        +{sampleLines.length - showLines.length} more
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
            <Box
              sx={{ display: 'flex', justifyContent: 'center', mt: 1, cursor: 'pointer' }}
              onClick={() => setExpanded((v) => !v)}
            >
              <Typography sx={{ fontSize: '0.68rem', color: 'primary.main' }}>
                {expanded ? 'Show less ↑' : 'Show more ↓'}
              </Typography>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ── List row view ─────────────────────────────────────────────────────

const TemplateListRow: React.FC<{ template: ReportTemplate }> = ({ template }) => {
  const sections = parseSections(template.structure_definition);
  const color = modalityColor(template.modality);
  return (
    <ListItem
      divider
      sx={{ py: 1, alignItems: 'flex-start', gap: 1 }}
    >
      <BookmarkIcon sx={{ fontSize: 16, color: `${color}.main`, mt: 0.25, flexShrink: 0 }} />
      <ListItemText
        primary={
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {template.exam_name || template.template_name}
            </Typography>
            {template.cpt_code && (
              <Typography variant="caption" color="text.disabled">
                CPT {template.cpt_code}
              </Typography>
            )}
            {!template.is_custom_prompt && (
              <Chip label="AUTO" size="small" variant="outlined" sx={{ height: 16, fontSize: '0.6rem' }} />
            )}
          </Stack>
        }
        secondary={
          sections.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
              {sections.map((s) => (
                <Chip key={s} label={s} size="small" sx={{ height: 18, fontSize: '0.62rem' }} />
              ))}
            </Stack>
          )
        }
      />
    </ListItem>
  );
};

// ── Modality group ────────────────────────────────────────────────────

const ModalityGroup: React.FC<{
  modality: string;
  templates: ReportTemplate[];
  viewMode: 'page' | 'list';
}> = ({ modality, templates, viewMode }) => {
  const [collapsed, setCollapsed] = useState(false);
  const color = modalityColor(modality);

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.25,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => setCollapsed((v) => !v)}
      >
        <Chip
          label={modality}
          size="small"
          color={color}
          sx={{ fontWeight: 700, fontSize: '0.72rem' }}
        />
        <Typography variant="body2" color="text.secondary">
          {templates.length}
        </Typography>
        <IconButton size="small">
          {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
        </IconButton>
      </Box>

      {!collapsed && (
        viewMode === 'page' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, 255px)',
              gap: 2.5,
              justifyContent: 'start',
            }}
          >
            {templates.map((t) => (
              <TemplateCard key={t.id} template={t} />
            ))}
          </Box>
        ) : (
          <List disablePadding dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {templates.map((t) => (
              <TemplateListRow key={t.id} template={t} />
            ))}
          </List>
        )
      )}
    </Box>
  );
};

// ── Create Template Dialog ────────────────────────────────────────────

const CreateTemplateDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}> = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState<'spec' | 'preview'>('spec');
  const [spec, setSpec] = useState('');
  const [name, setName] = useState('');
  const [modality, setModality] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<{ generated_prompt: string; structure_json?: Array<{ name: string }> } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) { setStep('spec'); setSpec(''); setName(''); setModality(''); setPreview(null); setError(null); }
  }, [open]);

  const handleGenerate = async () => {
    if (!spec.trim()) return;
    setGenerating(true);
    setError(null);
    try {
      const req: GenerateTemplateRequest = { natural_language_spec: spec, modality: modality || undefined, name: name || undefined };
      const res = await generateTemplatePreview(req);
      setPreview(res);
      setStep('preview');
    } catch {
      setError('Failed to generate preview. Try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!preview) return;
    setSaving(true);
    setError(null);
    try {
      await saveRadiologistTemplate({
        name: name || spec.slice(0, 60),
        natural_language_spec: spec,
        generated_prompt: preview.generated_prompt,
        structure_json: preview.structure_json,
        modality: modality || undefined,
      });
      onCreated();
      onClose();
    } catch {
      setError('Failed to save template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoFixHighIcon color="primary" />
          <span>{step === 'spec' ? 'Create Template' : 'Preview Generated Template'}</span>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {step === 'spec' ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Template Name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g. MRI Knee Post-Op"
            />
            <TextField
              label="Modality (optional)"
              value={modality}
              onChange={(e) => setModality(e.target.value)}
              size="small"
              placeholder="MRI, CT, US, XR…"
            />
            <TextField
              label="Describe what the template should cover"
              value={spec}
              onChange={(e) => setSpec(e.target.value)}
              multiline
              rows={5}
              fullWidth
              placeholder={
                'E.g. A comprehensive knee MRI template covering menisci, cruciate ligaments, ' +
                'collateral ligaments, articular cartilage, and joint fluid. ' +
                'Include a dedicated post-surgical changes section.'
              }
            />
            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Describe in plain English what sections and structures the report should cover.
                The AI will generate a structured template prompt.
              </Typography>
            </Box>
          </Stack>
        ) : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="success" icon={<AutoFixHighIcon />}>
              Template generated — review the sections below before saving
            </Alert>
            {preview?.structure_json && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Sections</Typography>
                <Stack direction="row" flexWrap="wrap" gap={0.75}>
                  {preview.structure_json.map((s, i) => (
                    <Chip key={i} label={s.name} size="small" color="primary" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            )}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Generated Prompt</Typography>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
                  borderRadius: 1,
                  maxHeight: 220,
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  color: 'text.primary',
                }}
              >
                {preview?.generated_prompt}
              </Box>
            </Box>
          </Stack>
        )}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        {step === 'preview' && (
          <Button onClick={() => setStep('spec')} disabled={saving}>Back</Button>
        )}
        <Button onClick={onClose} disabled={generating || saving}>Cancel</Button>
        {step === 'spec' ? (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generating || !spec.trim()}
            startIcon={generating ? <CircularProgress size={14} /> : <AutoFixHighIcon />}
          >
            {generating ? 'Generating…' : 'Preview Template'}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : <AddIcon />}
          >
            {saving ? 'Saving…' : 'Save Template'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────

const TemplatesPage: React.FC = () => {
  const [templates, setTemplates] = useState<ReportTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0); // 0 = My Templates, 1 = Public
  const [viewMode, setViewMode] = useState<'page' | 'list'>('page');
  const [createOpen, setCreateOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const list = await templateService.listTemplates();
      setTemplates(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // Filter
  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return templates.filter((t) => {
      const matchesSearch =
        !q ||
        t.template_name.toLowerCase().includes(q) ||
        (t.exam_name || '').toLowerCase().includes(q) ||
        (t.modality || '').toLowerCase().includes(q) ||
        (t.body_region || '').toLowerCase().includes(q) ||
        (t.cpt_code || '').toLowerCase().includes(q);
      // Tab 0 = My Templates (user-created), Tab 1 = Public (factory/system)
      const matchesTab = tab === 0 ? !t.is_factory_default : t.is_factory_default;
      return matchesSearch && matchesTab;
    });
  }, [templates, search, tab]);

  const grouped = useMemo(() => groupByModality(filtered), [filtered]);

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <PageHeaderNavigation
        title="Templates"
        subtitle="Manage your report templates"
      />

      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 2,
          mb: 2,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Search */}
        <TextField
          inputRef={searchRef}
          placeholder="Search templates…"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 260, maxWidth: 380, flex: 1 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Stack direction="row" spacing={1} alignItems="center">
          {/* View toggle */}
          <Tooltip title="List view">
            <IconButton
              size="small"
              onClick={() => setViewMode('list')}
              color={viewMode === 'list' ? 'primary' : 'default'}
            >
              <ViewListIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Card view">
            <IconButton
              size="small"
              onClick={() => setViewMode('page')}
              color={viewMode === 'page' ? 'primary' : 'default'}
            >
              <GridViewIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
          >
            Create Template
          </Button>
        </Stack>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <Tab
          label={
            <Stack direction="row" spacing={0.75} alignItems="center">
              <span>My Templates</span>
              <Chip
                label={templates.filter((t) => !t.is_factory_default).length}
                size="small"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            </Stack>
          }
        />
        <Tab
          label={
            <Stack direction="row" spacing={0.75} alignItems="center">
              <span>Public</span>
              <Chip
                label={templates.filter((t) => t.is_factory_default).length}
                size="small"
                sx={{ height: 18, fontSize: '0.65rem' }}
              />
            </Stack>
          }
        />
      </Tabs>

      {/* Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ py: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {search
              ? `No templates match "${search}"`
              : tab === 0
                ? 'No custom templates yet — create one to get started'
                : 'No system templates found'}
          </Typography>
        </Box>
      ) : (
        Array.from(grouped.entries()).map(([modality, tmplates]) => (
          <ModalityGroup
            key={modality}
            modality={modality}
            templates={tmplates}
            viewMode={viewMode}
          />
        ))
      )}

      {/* Create dialog */}
      <CreateTemplateDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setToast('Template saved successfully');
          reload();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplatesPage;
